import { setRole, setToken } from '@/utils/auth';
import {
  SignInRequest,
  SignInResponse,
  SignUpRequest,
  SignUpResponse,
} from '@/types/AuthType';
import { refetchAllForNewAccount, rtkApi } from '../rtkApi';

/** Signing in as someone else in an open tab must not show the last account's data. */
const onAccountSignIn = async (
  _arg: unknown,
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: (action: unknown) => unknown;
    queryFulfilled: Promise<{ data: SignInResponse | SignUpResponse }>;
  }
) => {
  const before = localStorage.getItem('user_id');
  try {
    const { data } = await queryFulfilled;
    if (data.token && data.user?._id !== before) {
      dispatch(refetchAllForNewAccount());
    }
  } catch {
    // the component reports sign-in errors
  }
};

export const authApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    signIn: builder.mutation<SignInResponse, SignInRequest>({
      query: (request) => ({
        url: '/auth/signin',
        method: 'POST',
        data: request,
      }),
      transformResponse: (response: SignInResponse) => {
        if (response.token) {
          setToken(response.token);
          setRole(response.role);
          // Store user ID in localStorage for later use
          if (response.user?._id) {
            localStorage.setItem('user_id', response.user._id);
          }
          // Remove navigation logic - let the component handle it
        }
        return response;
      },
      onQueryStarted: onAccountSignIn,
      invalidatesTags: ['User'],
    }),
    signUp: builder.mutation<SignUpResponse, SignUpRequest>({
      query: (request) => ({
        url: '/auth/signup',
        method: 'POST',
        data: request,
      }),
      transformResponse: (response: SignUpResponse) => {
        if (response.token) {
          setToken(response.token);
          setRole(response.role);
          if (response.user?._id) {
            localStorage.setItem('user_id', response.user._id);
          }
        }
        return response;
      },
      onQueryStarted: onAccountSignIn,
    }),
    verifySignIn: builder.mutation<SignInResponse, string>({
      query: (uid: string) => ({
        url: '/auth/signin/verify',
        method: 'post',
        data: { uid },
      }),
      transformResponse: (response: SignInResponse) => {
        if (response.token) {
          setToken(response.token);
          setRole(response.role);
          if (response.user?._id) {
            localStorage.setItem('user_id', response.user._id);
          }
        }
        return response;
      },
      onQueryStarted: onAccountSignIn,
      invalidatesTags: ['User'],
    }),
    updateSecurity: builder.mutation<void, boolean>({
      query: (status: boolean) => ({
        url: '/auth/security',
        method: 'put',
        data: { status },
      }),
      invalidatesTags: ['User'],
    }),
    verifySecurity: builder.mutation<void, string>({
      query: (uid: string) => ({
        url: '/auth/security/verify',
        method: 'post',
        data: { uid },
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useSignInMutation,
  useSignUpMutation,
  useVerifySecurityMutation,
  useUpdateSecurityMutation,
  useVerifySignInMutation,
} = authApi;
